import { Alert, Box, Typography } from '@mui/material';
import { useState } from 'react';
import { restoreFile, useRecentlyRemoved, type FileRef } from '@/api/files';
import { uploadErrorMessage } from '@/lib/storage-upload';
import { FileList } from './FileList';

/** "12 days left", counted to the end of a removed file's soft-delete period. */
export function daysLeft(iso: string | null | undefined): string {
  if (!iso) return '';
  const days = Math.ceil((Date.parse(iso) - Date.now()) / 86_400_000);
  return days <= 1 ? (days === 1 ? '1 day left' : 'less than a day left') : `${days} days left`;
}

/**
 * Files removed from one record — or from every proof under `parent` — that can still come back, each with a
 * Restore that puts it back under the record's own rules. Shows nothing when there are none.
 */
export function RecentlyRemoved({ refTable, refId, parent, field, onRestored }: {
  refTable: string;
  refId?: string | null;
  parent?: string | null;
  /** Only the files that were on this field of the record. */
  field?: string;
  onRestored?: (ref: FileRef) => void;
}) {
  const removed = useRecentlyRemoved(refTable, { refId, parent });
  const items = (removed.data ?? []).filter((f) => f.can_restore && (!field || f.ref_field === field));
  const [busy, setBusy] = useState<string>();
  const [error, setError] = useState<string>();
  if (!items.length && !error) return null;

  const restore = (id: string) => {
    setBusy(id);
    setError(undefined);
    restoreFile(id)
      .then((ref) => { removed.refetch(); onRestored?.(ref); })
      .catch((e) => setError(uploadErrorMessage(e)))
      .finally(() => setBusy(undefined));
  };

  return (
    <Box sx={{ mt: 1 }}>
      {items.length > 0 && (
        <Typography variant="caption" color="text.secondary">Recently removed ({items.length})</Typography>
      )}
      <FileList items={items.map((f) => ({
        key: f.id, name: f.name, size: f.size, note: daysLeft(f.purge_after),
        action: { label: busy === f.id ? 'Restoring…' : 'Restore', run: () => restore(f.id), disabled: !!busy },
      }))} />
      {error && <Alert severity="error" sx={{ mt: 0.5 }} onClose={() => setError(undefined)}>{error}</Alert>}
    </Box>
  );
}
